import { format } from 'url';

import { WithRouterProps } from 'next/dist/client/with-router';
import Router, { withRouter } from 'next/router';
import * as React from 'react';
import { compose } from 'recompose';

const MINUTES_TO_MILLIS = 1000 * 60;
const TEN_SECONDS_TO_MILLIS = 1000 * 10;

const MAX_HISTORY_LENGTH = 100;
const DEFAULT_SELECTED_TIME = 3;
const TIME_SELECTORS: Array<{ minutes: number; label: string; largeRange?: boolean }> = [
  { minutes: 30, label: '30m' },
  { minutes: 60, label: '1h' },
  { minutes: 60 * 4, label: '4h' },
  { minutes: 60 * 8, label: '8h' },
  { minutes: 60 * 16, label: '16h' },
  { minutes: 60 * 24, label: '24h' },
  { minutes: 60 * 48, label: '48h' },
  { minutes: 60 * 24 * 7, label: '1w' },
  { minutes: 60 * 24 * 30, label: '1m', largeRange: true },
];

// Support 10s, 30s, 60s, 5m and 10m.
const pollIntervals: number[] = [10000, 30000, 60000, 300000, 600000];

const roundTo10Seconds = (date: Date) => {
  return new Date(Math.round(date.getTime() / TEN_SECONDS_TO_MILLIS) * TEN_SECONDS_TO_MILLIS);
};

const floorTo10Seconds = (date: Date) => {
  return new Date(Math.floor(date.getTime() / TEN_SECONDS_TO_MILLIS) * TEN_SECONDS_TO_MILLIS);
};

const ceilTo10Seconds = (date: Date) => {
  return new Date(Math.ceil(date.getTime() / TEN_SECONDS_TO_MILLIS) * TEN_SECONDS_TO_MILLIS);
};

export interface State {
  from: Date;
  to: Date;
  open: boolean;
  selectedTimeSelector: number;
  selectedTimeHistory: Array<{ from: Date; to: Date; selectedTimeSelector?: number }>;
  prevSelectedTimeSelector: number;
  timeSelectors: Array<{ minutes: number; label: string; largeRange?: boolean }>;
  isLive: boolean;
  liveIntervals: number[];
  liveIntervalIndex: number;
  nextLiveUpdate: null | Date;
  closePicker: () => void;
  openPicker: () => void;
  setTime: (start: Date, end?: Date, disableLive?: boolean) => void;
  goBackInSelectedTimeHistory: () => void;
  setSelectedTimeselector: (index: number) => void;
  resetSelectedTimeselector: () => void;
  startLive: (liveIntervalIndex: number, cb?: any) => void;
  stopLive: (cb?: any) => void;
}

export const TimepickerContext = React.createContext<State>({
  to: new Date(),
  from: new Date(Date.now() - (8).hours),
  open: false,
  selectedTimeSelector: DEFAULT_SELECTED_TIME,
  selectedTimeHistory: [],
  prevSelectedTimeSelector: DEFAULT_SELECTED_TIME,
  timeSelectors: TIME_SELECTORS,
  isLive: false,
  nextLiveUpdate: null,
  liveIntervals: pollIntervals,
  liveIntervalIndex: -1,
  closePicker: () => {
    return;
  },
  openPicker: () => {
    return;
  },
  setTime: (start: Date, end?: Date, disableLive?: boolean) => {
    return;
  },
  goBackInSelectedTimeHistory: () => {
    return;
  },
  setSelectedTimeselector: (index: number) => {
    return;
  },
  resetSelectedTimeselector: () => {
    return;
  },
  startLive: (liveIntervalIndex: number, cb?: any) => {
    return;
  },
  stopLive: (cb?: any) => {
    return;
  },
});

const isASelectedTime = (start: Date, end: Date): number => {
  // Check if the selected interval still fits a timeSelector, or is a custom interval
  let selectedTimeSelector = DEFAULT_SELECTED_TIME;
  // If end is not within a 10 minutes of now(), its a custom time
  if (Math.abs(new Date().getTime() - end.getTime()) > 10 * MINUTES_TO_MILLIS) {
    // Select custom time selector interval
    selectedTimeSelector = TIME_SELECTORS.length;
  } else {
    const msDiff = ceilTo10Seconds(new Date(end)).getTime() - floorTo10Seconds(new Date(start)).getTime();
    selectedTimeSelector = TIME_SELECTORS.findIndex((sel) => {
      // Use a buffer range of 5 minutes when checking if it matches the selection.
      const bufferRange = 5 * MINUTES_TO_MILLIS;
      return (
        sel.minutes * MINUTES_TO_MILLIS <= msDiff + bufferRange &&
        sel.minutes * MINUTES_TO_MILLIS >= msDiff - bufferRange
      );
    });

    if (selectedTimeSelector === -1) {
      selectedTimeSelector = TIME_SELECTORS.length;
    }
  }
  return selectedTimeSelector;
};

interface Properties {
  children: React.ReactNode;
}

class TimepickerContextWrapper extends React.Component<Properties & WithRouterProps, State> {
  public readonly state: State;

  private liveTimer: NodeJS.Timer | undefined;

  constructor(props: Properties & WithRouterProps) {
    super(props);

    const { query } = props.router;

    let initialFrom = new Date(query.from as string);
    initialFrom = isFinite(initialFrom.getTime())
      ? initialFrom
      : floorTo10Seconds(
          new Date(new Date().getTime() - TIME_SELECTORS[DEFAULT_SELECTED_TIME].minutes * MINUTES_TO_MILLIS),
        );

    let initialTo = new Date(query.to as string);
    initialTo = isFinite(initialTo.getTime()) ? initialTo : ceilTo10Seconds(new Date());
    const selectedTimeSelector = isASelectedTime(initialFrom, initialTo);

    this.state = {
      from: initialFrom,
      to: initialTo,
      isLive: false,
      liveIntervalIndex: -1,
      open: false,
      liveIntervals: pollIntervals,
      selectedTimeSelector,
      nextLiveUpdate: null,
      selectedTimeHistory: [],
      prevSelectedTimeSelector: DEFAULT_SELECTED_TIME,
      timeSelectors: TIME_SELECTORS,
      closePicker: () => {
        this.setState({ open: false });
      },
      openPicker: () => {
        this.setState({ open: true });
      },
      setTime: (start: Date, end?: Date, disableLive: boolean = false) => {
        if (disableLive) {
          if (this.liveTimer) {
            clearInterval(this.liveTimer);
            delete this.liveTimer;
          }
        }
        if (!start) {
          return;
        }

        if (end === undefined) {
          let from;
          if (this.state.from !== null && this.state.to !== null) {
            from = new Date(
              start.getTime() - (new Date(this.state.to).getTime() - new Date(this.state.from).getTime()),
            );
          }
          if (this.state.selectedTimeSelector !== TIME_SELECTORS.length) {
            from = new Date(
              start.getTime() - TIME_SELECTORS[this.state.selectedTimeSelector].minutes * MINUTES_TO_MILLIS,
            );
          }

          if (from) {
            this.setFromTo(from, start, {
              open: false,
              nextLiveUpdate: disableLive ? null : this.state.nextLiveUpdate,
              liveIntervalIndex: disableLive ? -1 : this.state.liveIntervalIndex,
              isLive: disableLive ? false : this.state.isLive,
            });
          }
        } else {
          if (!end || end.getTime() > new Date().getTime()) {
            end = new Date();
          }
          // Check if the selected interval still fits a timeSelector, or is a custom interval
          const updatedSelectedTimeSelector = isASelectedTime(start, end);
          this.setFromTo(start, end, {
            open: false,
            selectedTimeSelector: updatedSelectedTimeSelector,
            prevSelectedTimeSelector: this.state.selectedTimeSelector,
            nextLiveUpdate: disableLive ? null : this.state.nextLiveUpdate,
            liveIntervalIndex: disableLive ? -1 : this.state.liveIntervalIndex,
            isLive: disableLive ? false : this.state.isLive,
          });
        }
      },
      goBackInSelectedTimeHistory: () => {
        if (this.state.selectedTimeHistory.length > 0) {
          const currentHistory = [...this.state.selectedTimeHistory];
          let revertTime = currentHistory.shift();
          // If the current time is the latest entry in the history, we need to shift one more time.
          if (
            revertTime &&
            this.state.from &&
            this.state.to &&
            revertTime.from.toISOString() === this.state.from.toISOString() &&
            revertTime.to.toISOString() === this.state.to.toISOString()
          ) {
            revertTime = currentHistory.shift();
          }
          if (revertTime) {
            this.setFromTo(revertTime.from, revertTime.to, {
              selectedTimeHistory: currentHistory,
              selectedTimeSelector:
                revertTime.selectedTimeSelector !== undefined && revertTime.selectedTimeSelector !== null
                  ? revertTime.selectedTimeSelector
                  : this.state.selectedTimeSelector,
              prevSelectedTimeSelector:
                revertTime.selectedTimeSelector !== undefined && revertTime.selectedTimeSelector !== null
                  ? this.state.selectedTimeSelector
                  : this.state.prevSelectedTimeSelector,
            });
          }
        }
      },
      setSelectedTimeselector: (index: number) => {
        let from = this.state.from;
        if (index !== TIME_SELECTORS.length) {
          from = new Date(new Date().getTime() - TIME_SELECTORS[index].minutes * MINUTES_TO_MILLIS);
        }

        if (from) {
          this.setFromTo(from, new Date(), {
            prevSelectedTimeSelector: this.state.selectedTimeSelector,
            selectedTimeSelector: index,
          });
        } else {
          this.setState({
            prevSelectedTimeSelector: this.state.selectedTimeSelector,
            selectedTimeSelector: index,
          });
        }
      },
      resetSelectedTimeselector: () => {
        let from = this.state.from;
        if (this.state.prevSelectedTimeSelector !== TIME_SELECTORS.length + 1) {
          from = new Date(
            new Date().getTime() - TIME_SELECTORS[this.state.prevSelectedTimeSelector].minutes * MINUTES_TO_MILLIS,
          );
        }
        if (from) {
          this.setFromTo(from, new Date(), {
            selectedTimeSelector: this.state.prevSelectedTimeSelector,
          });
        } else {
          this.setState({
            selectedTimeSelector: this.state.prevSelectedTimeSelector,
          });
        }
      },
      startLive: (liveIntervalIndex: number, cb?: any) => {
        // Verify that interval index is within possible values
        if (liveIntervalIndex !== -1 && liveIntervalIndex < pollIntervals.length) {
          if (!this.liveTimer) {
            // Start timer
            this.liveTimer = setInterval(this.checkLiveProgress, 1000);
          }
        }

        this.setState({
          liveIntervalIndex,
          isLive: true,
          nextLiveUpdate: roundTo10Seconds(new Date(new Date().getTime() + pollIntervals[liveIntervalIndex])),
        });
        if (cb) {
          cb();
        }
      },
      stopLive: (cb?: any) => {
        if (this.state.liveIntervalIndex !== -1 || this.state.isLive !== false || this.state.nextLiveUpdate !== null) {
          this.setState({ liveIntervalIndex: -1, isLive: false, nextLiveUpdate: null });
        }
        if (this.liveTimer) {
          clearInterval(this.liveTimer);
          delete this.liveTimer;
        }
        if (cb) {
          cb();
        }
      },
    };
  }

  /**
   * We are very conservative with our component causing rerenders, since it is very often wrapping
   * a significant amount of components.
   */
  public shouldComponentUpdate(nextProps: Properties & WithRouterProps, nextState: State) {
    if (
      this.state.from !== nextState.from ||
      this.state.to !== nextState.to ||
      this.state.open !== nextState.open ||
      this.state.selectedTimeSelector !== nextState.selectedTimeSelector ||
      this.state.liveIntervalIndex !== nextState.liveIntervalIndex
    ) {
      return true;
    }
    if (this.props.children !== nextProps.children) {
      return true;
    }
    return false;
  }

  // We use `getDerivedStateFromProps` to trigger time changes when the router updates.
  static getDerivedStateFromProps(props: Readonly<Properties & WithRouterProps>, state: State) {
    const routerFrom = Array.isArray(props?.router?.query?.from)
      ? props?.router?.query?.from?.[0]
      : props?.router?.query?.from;
    const routerTo = Array.isArray(props?.router?.query?.to) ? props?.router?.query?.to?.[0] : props?.router?.query?.to;

    if (!routerFrom || !routerTo) {
      return null;
    }

    const routesDoNotMatchState =
      (state.from === null && routerFrom) ||
      (state.from !== null && new Date(routerFrom).getTime() !== state.from?.getTime()) ||
      (state.to === null && routerTo) ||
      (state.to !== null && new Date(routerTo).getTime() !== state.to?.getTime());
    if ((routerFrom || routerTo) && routesDoNotMatchState) {
      return {
        from: new Date(routerFrom),
        to: new Date(routerTo),
      };
    }

    return null;
  }

  public componentWillUnmount() {
    this.state.stopLive();
  }

  public render() {
    const { children } = this.props;
    return <TimepickerContext.Provider value={this.state}>{children}</TimepickerContext.Provider>;
  }

  private checkLiveProgress = () => {
    const { nextLiveUpdate, liveIntervalIndex } = this.state;

    // Only live update if we are past the time the next update is scheduled for, and we
    // are not already loading data.
    if (nextLiveUpdate && nextLiveUpdate < new Date()) {
      if (this.state.from) {
        this.setFromTo(
          this.state.from,
          nextLiveUpdate,
          {
            nextLiveUpdate: roundTo10Seconds(new Date(Date.now() + pollIntervals[liveIntervalIndex])),
          },
          false,
        );
      } else {
        console.error('Missing a from time! Cannot perform live update');
      }
    } else if (!nextLiveUpdate) {
      if (this.liveTimer) {
        clearInterval(this.liveTimer);
        delete this.liveTimer;
      }
    }
  };

  private addTimeToHistory(from: Date | null, to: Date | null, selectedTimeSelector?: number) {
    const currentHistory = [...this.state.selectedTimeHistory];
    if (!from || !to) {
      return currentHistory;
    }
    // If the first element matches the date we just got, do not add it to the history.
    if (
      currentHistory[0] &&
      currentHistory[0].from.toISOString() === from.toISOString() &&
      currentHistory[0].to.toISOString() === to.toISOString()
    ) {
      return currentHistory;
    } else {
      // If we are growing above our history length, remove the last element.
      if (currentHistory.length >= MAX_HISTORY_LENGTH) {
        currentHistory.pop();
      }
      currentHistory.unshift({ from, to, selectedTimeSelector });
    }
    return currentHistory;
  }

  private setFromTo(from: Date, to: Date, newState: Partial<State> = {}, addToHistory: boolean = true) {
    const { pathname, query } = this.props.router;
    if (!newState.selectedTimeHistory && addToHistory) {
      newState.selectedTimeHistory = this.addTimeToHistory(
        this.state.from,
        this.state.to,
        this.state.selectedTimeSelector,
      );
    }
    // The router update here will trigger `getDerivedStateFromProps` that updates the from and to of the context.
    const url = format({
      pathname,
      query: {
        ...query,
        from: floorTo10Seconds(from).toISOString(),
        to: ceilTo10Seconds(to).toISOString(),
      },
    });
    Router.replace(url, url, { shallow: true });
    this.setState({
      ...(newState as State),
      from: floorTo10Seconds(from),
      to: ceilTo10Seconds(to),
    });
  }
}

const enhance = compose<any, Partial<Properties>>(withRouter);

export default enhance(TimepickerContextWrapper);
